import { useState } from 'react'
import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { strings } from './WelcomeEventForm.strings'
import { Button, Card, Image, Input, Spinner, TextArea } from '../../atoms'
import { Cropper } from '../Cropper/Cropper'
import { WelcomeEventData } from '@/ui/core/hooks/types'
import { useLayoutInfo } from '@/ui/core'

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1em;
  max-width: 600px;
  width: 100%;
  > p {
    color: ${({ theme }) => theme.colors.font.subdued};
  }
`
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1em;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  > p {
    margin: 0;
  }
`

const StyledCropper = styled(Cropper)<{ isMobile: boolean }>`
  max-width: 576px;
  /* max-width: ${({ isMobile }) => (isMobile ? '300px' : '576px')};
  max-height: ${({ isMobile }) => (isMobile ? '300px' : '576px')}; */
`

interface SubmitData extends Omit<WelcomeEventData, 'welcomeImage'> {
  welcomeImage?: File
  imagesToDelete?: string[]
}

interface Props {
  initialData?: WelcomeEventData & { images?: string[] }
  onSubmit: (data: SubmitData) => void
  isLoading?: boolean
}

export const WelcomeEventForm: React.FC<Props> = ({
  initialData,
  onSubmit,
  isLoading,
}) => {
  const intl = useIntl()
  const { size } = useLayoutInfo()

  const [displayImages, setDisplayImages] = useState<string[]>(
    initialData?.images ?? [],
  )
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

  const { images, ...rest } = initialData ?? {}

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WelcomeEventData>({
    defaultValues: rest,
  })

  const submit = handleSubmit(async (data) => {
    onSubmit({
      ...data,
      welcomeImage: data.welcomeImage,
      imagesToDelete: imagesToDelete,
    })
  })

  return (
    <StyledCard>
      <Header>
        <h1>
          <FormattedMessage {...strings.header} />
        </h1>
        <p>
          <FormattedMessage {...strings.subheader} />
        </p>
      </Header>
      <Form onSubmit={submit} data-testid="form">
        <Input
          label={intl.formatMessage(strings.headerLabel)}
          error={
            errors.welcomeHeader &&
            intl.formatMessage(strings.errorLabel, {
              field: intl.formatMessage(strings.headerName),
            })
          }
          {...register('welcomeHeader')}
          required
        />
        <TextArea
          label={intl.formatMessage(strings.messageLabel)}
          error={
            errors.welcomeMessage &&
            intl.formatMessage(strings.errorLabel, {
              field: intl.formatMessage(strings.messageName),
            })
          }
          {...register('welcomeMessage')}
        />
        {displayImages
          ? displayImages.map((imgSrc) => {
              return (
                <Image
                  key={imgSrc}
                  imgSrc={imgSrc}
                  alt={imgSrc.slice(imgSrc.indexOf('.'))}
                  btnLabel="X"
                  btnAction={() => {
                    const updatedImages = images?.filter(
                      (img) => img !== imgSrc,
                    )
                    setDisplayImages(updatedImages ?? [])
                    setImagesToDelete([...imagesToDelete, imgSrc])
                  }}
                />
              )
            })
          : null}
        <Controller
          name="welcomeImage"
          control={control}
          render={({ field }) => (
            <StyledCropper
              label={intl.formatMessage(strings.uploadPhotoBtn)}
              onChange={field.onChange}
              isMobile={size === 'narrow'}
            />
          )}
        />
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Spinner />}
          <FormattedMessage {...strings.submitButton} />
        </Button>
      </Form>
    </StyledCard>
  )
}
